import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Order,
  OrderCreate,
  OrderUpdate,
  OrderFilters,
} from "@cold-crew/shared";
import type { PaginatedResponse } from "@cold-crew/shared";

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const orderKeys = {
  all:    () => ["pedidos"]                         as const,
  lists:  () => [...orderKeys.all(), "list"]        as const,
  list:   (f: Partial<OrderFilters>) =>
            [...orderKeys.lists(), f]               as const,
  detail: (id: number) =>
            [...orderKeys.all(), "detail", id]      as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useOrders(filters: Partial<OrderFilters> = {}) {
  return useQuery({
    queryKey: orderKeys.list(filters),
    queryFn:  () =>
      api.get<PaginatedResponse<Order>>("/pedidos", filters as Record<string, string | number | boolean | undefined | null>),
  });
}

// ─── Single ───────────────────────────────────────────────────────────────────

export function useOrder(id: number) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn:  () => api.get<Order>(`/pedidos/${id}`),
    enabled:  !!id,
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OrderCreate) => api.post<Order>("/pedidos", data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: orderKeys.lists() }),
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export function useUpdateOrder(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OrderUpdate) => api.put<Order>(`/pedidos/${id}`, data),
    onSuccess:  (updated) => {
      qc.invalidateQueries({ queryKey: orderKeys.lists() });
      qc.setQueryData(orderKeys.detail(id), updated);
    },
  });
}

// ─── Delete (soft) ────────────────────────────────────────────────────────────

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/pedidos/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: orderKeys.lists() }),
  });
}

// ─── Restore ─────────────────────────────────────────────────────────────────

export function useRestoreOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Order>(`/pedidos/${id}/restore`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: orderKeys.lists() }),
  });
}
